import { Option, Result } from '@sapphire/result';

import { Nullable, spreadIf } from '@ps/utils';

import {
  changedSharedMetadata,
  EMPTY_ADVANCED_FEASIBILITY_FILTERS,
  LaunchPayloadAccess,
  LaunchSameSettings,
  MC_MESSAGES,
  ProjectMemberSync,
  expectedEntryLinks,
  ProjectUpdateChildInput,
  ProjectUpdatePayloadInput,
  INITIAL_LOCATION_UPLOAD_STATE,
  ScheduleLaunch,
  StandardSurveyPayloadInput,
  Survey,
  SurveyKind,
  SurveyLaunchForm,
  SurveyLaunchFormMapper,
  SurveyLaunchFormState,
  SurveyLocaleData,
  SurveyMetadataForm,
  SurveyPricingSnapshot,
  surveyToMetadataForm,
  TrackerUpdateChildInput,
} from '@ps/survey-utils';

import { SurveyAudienceForm } from '../../../utils';
import { InsightsChildState } from '../../insights/survey-creation-insights-state.model';
import { SurveyChildKey } from '../../survey-creation-shared-state.model';
import { SurveyChildEntry } from '../../survey-creation-child-registry.helper';
import { localePricing } from '../domain/child-pricing';
import { marginSnapshot } from '../domain/margin-snapshot';

export interface ProjectSaveMessages {
  readonly noChildrenSelected: string;
  readonly childNotSaveable: string;
}

export interface ProjectMemberSyncInput {
  readonly sameSettings: LaunchSameSettings;
  readonly childLaunches: Readonly<
    Record<SurveyChildKey, SurveyLaunchFormState>
  >;
}

export interface ProjectUpdateInput {
  /** The project row, which the root of the payload is built from. */
  readonly project: Survey;
  readonly activeMetadata: SurveyMetadataForm;
  readonly children: readonly SurveyChildEntry[];
  readonly launch: SurveyLaunchForm;
  /**
   * The family's "same …" choices and every child's launch form as the launch
   * page holds it, `launch` being the active child's. A project kind that
   * `ProjectMemberSync` serves saves each child with its held form; any other
   * keeps its launch on the project alone.
   */
  readonly memberSync?: ProjectMemberSyncInput;
  readonly launchAccess: LaunchPayloadAccess;
  readonly pricing: SurveyPricingSnapshot;
  readonly insightsChildren: Record<SurveyChildKey, InsightsChildState>;
  readonly userId: number;
  readonly companyId: number;
  readonly enableRespondentStatusFilter: boolean;
  readonly messages: ProjectSaveMessages;
  /** Only an operator writes the Service Operator settings. */
  readonly isOperator: boolean;
  /** The child the launch form on screen belongs to. */
  readonly activeChildKey: Nullable<SurveyChildKey>;
}

/**
 * The project's payload with one more child on it, cloned from the first
 * participant and re-pointed at `locale`. No `surveyId` is what asks the
 * endpoint for a new row.
 *
 * The clone is deliberate: targeting and offer_price are hoist exceptions
 * (`MC_GROUPED_UPDATE_HOIST_EXCEPTIONS`), so a child stating none would be
 * created bare rather than inheriting the group's. Anything the locales already
 * agree on hoists to the root, so the clone only decides where they differ.
 */
export function appendProjectChild(
  input: ProjectUpdateInput,
  locale: SurveyLocaleData,
): Result<ProjectUpdatePayloadInput, string> {
  // The new locale states no launch of its own: it takes the project's,
  // never the first locale's own URLs or file.
  return appendChild(
    input,
    (payload, { launch: _firstLocaleLaunch, ...source }) =>
      Result.ok<ProjectUpdatePayloadInput>({
        ...payload,
        children: [
          ...payload.children,
          {
            ...source,
            surveyId: undefined,
            metadata: {
              ...source.metadata,
              selectedCountries: [locale.countryCode],
              selectedLanguages: [locale.languageShortCode],
            },
          },
        ],
      }),
  );
}

export function appendTrackerWave(
  input: ProjectUpdateInput,
  activeChildKey: Nullable<SurveyChildKey>,
  hasGlobalPriceOverride: boolean,
): Result<ProjectUpdatePayloadInput<TrackerUpdateChildInput>, string> {
  return appendChild(input, (payload, source) =>
    activeWaveLocale(input.children, activeChildKey).match({
      some: (locale) =>
        Result.ok<ProjectUpdatePayloadInput<TrackerUpdateChildInput>>({
          ...payload,
          children: [
            ...payload.children,
            newWave(source, locale, hasGlobalPriceOverride),
          ],
        }),
      none: () => Result.err(input.messages.childNotSaveable),
    }),
  );
}

export function removeTrackerWave(
  input: ProjectUpdateInput,
  surveyId: number,
): Result<ProjectUpdatePayloadInput<TrackerUpdateChildInput>, string> {
  return buildProjectUpdate(input).map((payload) => ({
    ...payload,
    children: payload.children
      .filter((child) => child.surveyId !== surveyId)
      .map((child) => ({
        ...child,
        surveyGrouping: child.surveyGrouping?.filter((id) => id !== surveyId),
      })),
  }));
}

export function updateTrackerWaveLaunch(
  input: ProjectUpdateInput,
  surveyId: number,
  scheduleLaunch: ScheduleLaunch,
): Result<ProjectUpdatePayloadInput<TrackerUpdateChildInput>, string> {
  return buildProjectUpdate(input).map((payload) => ({
    ...payload,
    children: payload.children.map((child) =>
      child.surveyId === surveyId ? { ...child, scheduleLaunch } : child,
    ),
  }));
}

// `buildProjectUpdate` refuses a project with no participants, so the payload
// always has a first child to clone from.
function appendChild<T extends ProjectUpdateChildInput>(
  input: ProjectUpdateInput,
  build: (
    payload: ProjectUpdatePayloadInput<TrackerUpdateChildInput>,
    source: TrackerUpdateChildInput,
  ) => Result<ProjectUpdatePayloadInput<T>, string>,
): Result<ProjectUpdatePayloadInput<T>, string> {
  return buildProjectUpdate(input).andThen((payload) =>
    build(payload, payload.children[0]),
  );
}

function newWave(
  source: TrackerUpdateChildInput,
  locale: SurveyLocaleData,
  hasGlobalPriceOverride: boolean,
): TrackerUpdateChildInput {
  return {
    surveyId: undefined,
    metadata: {
      ...source.metadata,
      selectedCountries: [locale.countryCode],
      selectedLanguages: [locale.languageShortCode],
    },
    pricing: {
      ...source.pricing,
      offerPrice: hasGlobalPriceOverride ? source.pricing.offerPrice : null,
    },
    userId: source.userId,
    companyId: source.companyId,
    targeting: {
      qualifications: [],
      quotas: [],
      locationFileUpload: INITIAL_LOCATION_UPLOAD_STATE,
    },
    advancedFeasibilityFilters: EMPTY_ADVANCED_FEASIBILITY_FILTERS,
    enableRespondentStatusFilter: source.enableRespondentStatusFilter,
    templateId: source.templateId,
  };
}

function activeWaveLocale(
  children: readonly SurveyChildEntry[],
  activeChildKey: Nullable<SurveyChildKey>,
): Option<SurveyLocaleData> {
  const active = children.find(([key]) => isActiveChild(key, activeChildKey));
  return Option.from(active?.[2].survey).map(
    ({ countryCode, languageShortCode }) => ({
      countryCode,
      languageShortCode,
    }),
  );
}

/** The project's own body plus one entry per child, ready for the PUT. */
export function buildProjectUpdate(
  input: ProjectUpdateInput,
): Result<ProjectUpdatePayloadInput<TrackerUpdateChildInput>, string> {
  const participants = participantEntries(input);
  if (!participants.length) {
    return Result.err(input.messages.noChildrenSelected);
  }

  const sync = memberSyncOf(input);
  return sharedFileCoversLocales(input, sync, participants).andThen(() =>
    Result.all(
      participants.map((entry) => projectUpdateChild(entry, input, sync)),
    ).map((children) => ({ project: projectInput(input, sync), children })),
  );
}

// A Group MC or Tracker saves every child with the launch form the launch page
// holds for it; any other project kind has no held forms to save from.
function memberSyncOf({
  project,
  launch,
  memberSync,
}: ProjectUpdateInput): Option<ProjectMemberSync> {
  return Option.from(memberSync)
    .filter(() => SurveyKind.isGroupMcOrTracker(project))
    .map(({ sameSettings, childLaunches }) =>
      ProjectMemberSync.from({ launch, sameSettings, childLaunches }),
    );
}

// However "same File Links" came to be checked, a file shared by every locale
// or wave must cover each of their completes at their own IR before it goes out.
function sharedFileCoversLocales(
  input: ProjectUpdateInput,
  sync: Option<ProjectMemberSync>,
  participants: readonly SurveyChildEntry[],
): Result<void, string> {
  const { sameSettingsForAllSurveys, uniqueLinksEnabled, entryLinks } =
    input.launch.uniqueLinks;
  if (
    sync.isNone() ||
    !sameSettingsForAllSurveys ||
    !uniqueLinksEnabled ||
    !entryLinks.length
  ) {
    return Result.ok(undefined);
  }
  const expectedLinks = expectedEntryLinks(
    participants.flatMap(([, , { survey }]) => (survey ? [survey] : [])),
  );
  return entryLinks.length < expectedLinks
    ? Result.err(
        MC_MESSAGES.UNIQUE_LINKS_BELOW_EXPECTED.replace(
          '<expectedLinks>',
          String(expectedLinks),
        ),
      )
    : Result.ok(undefined);
}

// The registry holds the project row alongside its locales and only the
// locales are participants, so this drops the project. Registry order is kept
// as-is — it is keyed by survey id, so the first entry is the lowest-id locale,
// not the drawer's first row (`orderedChildEntries` owns that ordering).
function participantEntries({
  children,
}: ProjectUpdateInput): readonly SurveyChildEntry[] {
  return children.filter(([, , { participant }]) => !!participant);
}

// The project carries the launch settings and no targeting; its locales own the
// targeting and their own launch values (see childLaunch).
function projectInput(
  input: ProjectUpdateInput,
  sync: Option<ProjectMemberSync>,
): StandardSurveyPayloadInput {
  const {
    project,
    activeMetadata,
    launchAccess,
    pricing,
    userId,
    companyId,
    enableRespondentStatusFilter,
  } = input;
  const projectMetadata = surveyToMetadataForm(project);
  const launch = projectLaunch(input, sync);

  return {
    metadata: {
      ...projectMetadata,
      // Only MC-grouped/Tracker share metadata with the root.
      ...(SurveyKind.isGroupMcOrTracker(project)
        ? changedSharedMetadata(projectMetadata, activeMetadata)
        : {}),
    },
    pricing,
    userId,
    companyId,
    targeting: {
      qualifications: [],
      quotas: [],
      locationFileUpload: INITIAL_LOCATION_UPLOAD_STATE,
    },
    launch,
    launchAccess: {
      ...launchAccess,
      canEditServiceOperator:
        input.isOperator &&
        ProjectMemberSync.sharesServiceOperator(project, input.launch),
    },
    enableRespondentStatusFilter,
    billingId: projectBillingId(project, launch),
  };
}

function projectUpdateChild(
  [key, child, { survey }]: SurveyChildEntry,
  input: ProjectUpdateInput,
  sync: Option<ProjectMemberSync>,
): Result<TrackerUpdateChildInput, string> {
  const { messages } = input;
  if (!survey) {
    return Result.err(messages.childNotSaveable);
  }

  const form = SurveyAudienceForm.from({
    metadata: child.metadata,
    qualifications: child.qualifications,
    quotas: child.quotas,
  });
  const { data } = form;
  if (!data) {
    return Result.err(form.error ?? messages.childNotSaveable);
  }

  const launch = childLaunch(key, survey, input, sync);
  return Result.ok<TrackerUpdateChildInput>({
    surveyId: survey.id,
    launchAccess: input.launchAccess,
    metadata: data.metadata,
    pricing: localePricing(
      input.pricing,
      input.insightsChildren[key],
      survey.offerPrice ?? null,
    ),
    userId: input.userId,
    companyId: input.companyId,
    targeting: {
      qualifications: data.qualifications,
      quotas: data.quotas,
      locationFileUpload: child.locationFileUpload,
    },
    advancedFeasibilityFilters: child.advancedFeasibilityFilters,
    enableRespondentStatusFilter: input.enableRespondentStatusFilter,
    ...marginSnapshot(child.margin).mapOr({}, (snapshot) => ({
      margin: {
        ...snapshot,
        buyerCpi: snapshot.userAcceptedMargin ? snapshot.buyerCpi : null,
      },
    })),
    ...ProjectMemberSync.childSpecificFields(survey),
    launch,
    billingId: childBillingId(key, survey, launch, input, sync),
    ...activeChildServiceOperator(key, input),
  });
}

// Which launch form each row of the PUT is saved with — `hoistCommonFields`
// then leaves per child only what differs:
// - the child on screen: the form being edited;
// - a Group MC or Tracker: every other child and the project row the form the
//   launch page holds for it, shared sections already copied on;
// - a Grouped: every other child the form the launch page holds for it, and
//   the project row the form being edited;
// - any other project kind: every other child its own saved launch, and the
//   project row the form being edited.
function projectLaunch(
  input: ProjectUpdateInput,
  sync: Option<ProjectMemberSync>,
): SurveyLaunchForm {
  return sync.mapOr(input.launch, (held) =>
    held.launchForProject(input.project),
  );
}

function childLaunch(
  key: SurveyChildKey,
  survey: Survey,
  input: ProjectUpdateInput,
  sync: Option<ProjectMemberSync>,
): SurveyLaunchForm {
  if (SurveyKind.isGrouped(input.project)) {
    return groupedChildLaunch(key, survey, input);
  }
  return sync.mapOrElse(
    () => new SurveyLaunchFormMapper().fromSurvey(survey),
    (held) =>
      isActiveChild(key, input.activeChildKey)
        ? input.launch
        : held.childLaunchOf(survey),
  );
}

// Legacy: a Grouped child owns its launch settings, so the edit is written to
// the child on screen and every other child keeps the form the page holds for
// it. Its saved survey can't stand in: the PUT answers with the project row
// only, so a child left after an edit would be saved back to its old values.
function groupedChildLaunch(
  key: SurveyChildKey,
  survey: Survey,
  input: ProjectUpdateInput,
): SurveyLaunchForm {
  return withOwnTestUrl(
    isActiveChild(key, input.activeChildKey)
      ? input.launch
      : heldChildLaunch(survey, input),
  );
}

function heldChildLaunch(
  survey: Survey,
  { launch, memberSync }: ProjectUpdateInput,
): SurveyLaunchForm {
  return Option.from(memberSync).mapOrElse(
    () => new SurveyLaunchFormMapper().fromSurvey(survey),
    ({ sameSettings, childLaunches }) =>
      ProjectMemberSync.from({
        launch,
        sameSettings,
        childLaunches,
      }).childLaunchOf(survey),
  );
}

// The endpoint spreads the project root into every child and an empty
// test_url is left off the payload, so a child without one would keep a stale
// value or take the root's edit. Legacy: test_url falls back to the live URL.
function withOwnTestUrl(launch: SurveyLaunchForm): SurveyLaunchForm {
  return {
    ...launch,
    surveyUrls: {
      ...launch.surveyUrls,
      testUrl: launch.surveyUrls.testUrl || launch.surveyUrls.liveUrl,
    },
  };
}

// A Group MC shares one billing id, so it is saved with the one its project's
// launch form holds, edits included; any other project row keeps its own.
function projectBillingId(project: Survey, launch: SurveyLaunchForm): string {
  return SurveyKind.isGroupMc(project)
    ? launch.billingDetails.billingId
    : project.billingId;
}

// A Group MC locale is always saved with its project's billing id, whichever
// page saves it. Any other child on screen is saved with the billing id being
// edited, one whose launch form the page holds with that form's, and the rest
// with their own saved one.
function childBillingId(
  key: SurveyChildKey,
  survey: Survey,
  launch: SurveyLaunchForm,
  input: ProjectUpdateInput,
  sync: Option<ProjectMemberSync>,
): string {
  if (SurveyKind.isGroupMc(survey)) {
    return projectBillingId(input.project, projectLaunch(input, sync));
  }
  if (isActiveChild(key, input.activeChildKey)) {
    return input.launch.billingDetails.billingId;
  }
  return sync.isSome() || SurveyKind.isGrouped(input.project)
    ? launch.billingDetails.billingId
    : survey.billingId;
}

// The endpoint spreads the project root into every child, so Service Operator
// settings shared by all ride on the root; otherwise only the child on screen
// gets them.
function activeChildServiceOperator(
  key: SurveyChildKey,
  input: ProjectUpdateInput,
): Pick<TrackerUpdateChildInput, 'serviceOperator'> {
  return spreadIf(
    input.isOperator &&
      !ProjectMemberSync.sharesServiceOperator(input.project, input.launch) &&
      isActiveChild(key, input.activeChildKey),
    { serviceOperator: input.launch.serviceOperator },
  );
}

// Registry keys come from Object.entries (strings); the active key may be a survey id.
function isActiveChild(
  key: SurveyChildKey,
  activeChildKey: Nullable<SurveyChildKey>,
): boolean {
  return String(key) === String(activeChildKey);
}
